import { useState, useEffect } from "react";
import { supabase } from "./supabase";

export function useAuth() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    if (!supabase) return { error: new Error("Supabase not configured") };
    return supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  };

  const signInWithEmail = async (email) => {
    if (!supabase) return { error: new Error("Supabase not configured") };
    return supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
  };

  const signOut = async () => {
    if (!supabase) return;
    return supabase.auth.signOut();
  };

  return {
    session,
    user: session?.user ?? null,
    token: session?.access_token ?? null,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signOut,
  };
}

export function SignInModal({ open, onClose, signInWithGoogle, signInWithEmail }) {
  const [mode, setMode]           = useState("choose"); // choose | email | email_sent
  const [email, setEmail]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  if (!open) return null;

  const handleGoogle = async () => {
    setSubmitting(true); setError(null);
    const { error: err } = await signInWithGoogle();
    if (err) {
      setError(err.message || "Couldn't sign in with Google.");
      setSubmitting(false);
    }
    // Success path: browser redirects to Google; no UI cleanup needed.
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true); setError(null);
    const { error: err } = await signInWithEmail(email.trim());
    setSubmitting(false);
    if (err) setError(err.message || "Couldn't send magic link.");
    else setMode("email_sent");
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2 className="modal-title">Sign in</h2>
        <p className="modal-sub">Save your verdicts. Buy credits. Share your look.</p>

        {mode === "choose" && (
          <>
            <button className="auth-btn auth-btn-google" onClick={handleGoogle} disabled={submitting}>
              Continue with Google
            </button>
            <button className="auth-btn" onClick={() => setMode("email")} disabled={submitting}>
              Sign in with email
            </button>
          </>
        )}

        {mode === "email" && (
          <form onSubmit={handleEmail}>
            <input
              className="email-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            <button className="auth-btn" type="submit" disabled={submitting || !email.trim()}>
              {submitting ? "Sending…" : "Send magic link"}
            </button>
            <button type="button" className="auth-back" onClick={() => setMode("choose")}>← Back</button>
          </form>
        )}

        {mode === "email_sent" && (
          <div className="email-sent">
            Check <strong>{email}</strong> for a magic link. Click it to finish signing in.
          </div>
        )}

        {error && <div className="modal-error">{error}</div>}
      </div>
    </div>
  );
}
